import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SidebarContent } from './Sidebar';
import { Topbar } from './Topbar';
import { MobileNav } from './MobileNav';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { cn } from '@/lib/utils/cn';

export function AppShell() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();

  const drawerRef = React.useRef<HTMLElement>(null);
  const lastFocusRef = React.useRef<HTMLElement | null>(null);

  // Close the drawer whenever the route changes (e.g. after tapping a nav link).
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Drawer a11y: focus into it, trap Tab, Escape to close, lock body scroll,
  // and return focus to the trigger on close.
  React.useEffect(() => {
    if (!mobileOpen) return;
    lastFocusRef.current = document.activeElement as HTMLElement | null;
    drawerRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        return;
      }
      if (e.key === 'Tab' && drawerRef.current) {
        const nodes = drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])',
        );
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      lastFocusRef.current?.focus?.();
    };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 start-0 z-40 hidden w-64 border-e border-sidebar-border lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside
            ref={drawerRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className={cn(
              'absolute inset-y-0 start-0 w-72 max-w-[86%] shadow-2xl focus:outline-none',
              'motion-safe:animate-in motion-safe:slide-in-from-left motion-safe:duration-300 motion-safe:ease-out',
              'rtl:motion-safe:slide-in-from-right',
            )}
          >
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      ) : null}

      {/* Main column */}
      <div className="lg:ps-64">
        <Topbar />
        <main
          className={cn(
            'mx-auto w-full max-w-[1600px] px-4 py-4 sm:px-6 sm:py-6',
            // Clear the fixed bottom nav (+ safe area) on phones; normal on lg+.
            'pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-8',
          )}
        >
          <ErrorBoundary resetKey={location.pathname}>
            {/* Keyed wrapper gives a subtle fade between routes for a smoother feel. */}
            <div
              key={location.pathname}
              className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
            >
              <Outlet />
            </div>
          </ErrorBoundary>
        </main>
      </div>

      {/* Phone-only bottom navigation */}
      <MobileNav onOpenMenu={() => setMobileOpen(true)} />
    </div>
  );
}
